// Users: every account with key engagement stats. Searchable, sortable, and each
// row expands to that player's leagues + most recent picks.
import { Fragment, useMemo, useState } from 'react';
import { Bell, Crown, Search } from 'lucide-react';
import { tierName } from '@h2h/shared';
import { cap } from '../../lib/format';
import { Avatar } from '../../components/ui/bits';
import type { AdminEntryRow } from '../../lib/adminApi';
import { EmptyState, EntryCard, PageTitle, Panel, Pill, Td, Th, formatDate, timeAgo, useAdminData } from './parts';

type SortKey = 'name' | 'createdAt' | 'leaguesJoined' | 'picksCount' | 'lastActiveAt';

export default function Users() {
  const { data } = useAdminData();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const [expanded, setExpanded] = useState<string | null>(null);

  const leagueName = useMemo(() => new Map(data.leagues.map((l) => [l.leagueId, l.name])), [data.leagues]);
  const entriesByUser = useMemo(() => {
    const m = new Map<string, AdminEntryRow[]>();
    for (const e of data.entries) {
      const list = m.get(e.userId) ?? [];
      list.push(e);
      m.set(e.userId, list);
    }
    return m;
  }, [data.entries]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = data.users;
    if (q) {
      list = list.filter(
        (u) => u.displayName.toLowerCase().includes(q) || (u.username ?? '').toLowerCase().includes(q)
      );
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.key === 'name') return a.displayName.localeCompare(b.displayName) * dir;
      return ((a[sort.key] as number) - (b[sort.key] as number)) * dir;
    });
  }, [data.users, search, sort]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  }
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <>
      <PageTitle title="Users" sub={`${data.users.length} accounts`} />

      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-black/10 bg-card px-3.5 py-2.5 shadow-tile md:max-w-sm">
        <Search size={17} className="text-ink3" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or @username"
          className="w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-ink3"
        />
      </div>

      <Panel>
        {rows.length === 0 ? (
          <EmptyState>No users match “{search}”.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-black/5 bg-wash">
                  <Th onClick={() => toggleSort('name')} active={sort.key === 'name'}>
                    Player{arrow('name')}
                  </Th>
                  <Th onClick={() => toggleSort('createdAt')} active={sort.key === 'createdAt'}>
                    Joined{arrow('createdAt')}
                  </Th>
                  <Th onClick={() => toggleSort('leaguesJoined')} active={sort.key === 'leaguesJoined'} className="text-right">
                    Leagues{arrow('leaguesJoined')}
                  </Th>
                  <Th onClick={() => toggleSort('picksCount')} active={sort.key === 'picksCount'} className="text-right">
                    Picks{arrow('picksCount')}
                  </Th>
                  <Th onClick={() => toggleSort('lastActiveAt')} active={sort.key === 'lastActiveAt'}>
                    Last active{arrow('lastActiveAt')}
                  </Th>
                  <Th>Flags</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const isOpen = expanded === u.userId;
                  const recent = entriesByUser.get(u.userId) ?? [];
                  return (
                    <Fragment key={u.userId}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : u.userId)}
                        className="cursor-pointer border-b border-black/5 transition-colors hover:bg-wash"
                      >
                        <Td>
                          <div className="flex items-center gap-3">
                            <Avatar name={u.displayName} tone={u.avatarTone} size={34} />
                            <div className="min-w-0">
                              <div className="truncate font-bold tracking-tight">{u.displayName}</div>
                              {u.username && <div className="truncate text-[12px] text-ink2">@{u.username}</div>}
                            </div>
                          </div>
                        </Td>
                        <Td className="text-ink2">{formatDate(u.createdAt)}</Td>
                        <Td className="num text-right font-semibold">{u.leaguesJoined}</Td>
                        <Td className="num text-right font-semibold">{u.picksCount}</Td>
                        <Td className="text-ink2">{timeAgo(u.lastActiveAt)}</Td>
                        <Td>
                          <div className="flex items-center gap-1.5">
                            {u.hasPushToken && (
                              <span title="Has push token" className="text-accent-ink">
                                <Bell size={15} />
                              </span>
                            )}
                            {u.proWaitlist && (
                              <span title="Pro waitlist" className="text-gold-ink">
                                <Crown size={15} />
                              </span>
                            )}
                            {!u.profileComplete && <Pill tone="danger">Incomplete</Pill>}
                          </div>
                        </Td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-black/5 bg-wash/60">
                          <td colSpan={6} className="px-4 py-4 md:px-5">
                            <div className="grid gap-5 md:grid-cols-2">
                              <div>
                                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-ink2">
                                  Leagues
                                </h3>
                                {u.memberships.length === 0 ? (
                                  <p className="text-[13px] text-ink2">No leagues joined.</p>
                                ) : (
                                  <ul className="space-y-1.5">
                                    {u.memberships.map((m) => (
                                      <li
                                        key={m.leagueId}
                                        className="flex items-center justify-between gap-2 rounded-xl bg-card px-3 py-2 text-[13px] shadow-tile"
                                      >
                                        <span className="truncate font-semibold">{leagueName.get(m.leagueId) ?? m.leagueId}</span>
                                        <span className="flex shrink-0 items-center gap-2 text-ink2">
                                          <Pill tone="gold">{cap(tierName(m.tier))}</Pill>
                                          <span className="num">{m.roundsPlayed} rds</span>
                                          <span className="num">{m.seasonPoints} pts</span>
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div>
                                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-ink2">
                                  Recent picks
                                </h3>
                                {recent.length === 0 ? (
                                  <p className="text-[13px] text-ink2">No picks in this window.</p>
                                ) : (
                                  <div className="space-y-3">
                                    {recent.slice(0, 3).map((e) => (
                                      <EntryCard key={`${e.leagueId}-${e.roundId}`} entry={e} showWho={false} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
